/**
 * Package receipt promo text helpers.
 *
 * `packages.promo_message` is free-form text. Line breaks can be entered as:
 * - a real newline character in the DB string, or
 * - the two-character escape `\n` (backslash + n) — handy for SQL/manual edits
 *
 * Every non-empty line is rendered bold and center-aligned on both PDF and ESC/POS
 * receipts. Empty lines between content are preserved as vertical spacing.
 */

/**
 * Keep printable Latin-1 only (0x20–0x7E, 0xA0–0xFF).
 *
 * ESC/POS encodes with `charCodeAt(i) & 0xff`, so higher Unicode (e.g. Cyrillic Л =
 * U+041B) would otherwise downcast into control bytes like ESC (0x1B) and enable
 * printer-command injection. Whitelisting Latin-1 printable blocks that path.
 */
export function sanitizePromoPrintableLine(value: string): string {
  return value
    .replace(/[^\x20-\x7E\xA0-\xFF]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Splits a package promo_message into printable lines.
 * Returns [] when there is no printable content.
 */
export function parsePromoMessageLines(raw: string | null | undefined): string[] {
  if (raw == null) return [];

  const normalized = String(raw)
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    // Typed "\n" escapes from SQL/manual DB edits → real line breaks.
    .replace(/\\n/g, '\n');

  const lines = normalized.split('\n').map((line) => sanitizePromoPrintableLine(line));

  let start = 0;
  let end = lines.length;
  while (start < end && lines[start] === '') start += 1;
  while (end > start && lines[end - 1] === '') end -= 1;
  return lines.slice(start, end);
}

/** True when the promo message has at least one non-empty printable line. */
export function hasPromoMessageContent(raw: string | null | undefined): boolean {
  return parsePromoMessageLines(raw).some((line) => line !== '');
}
